import { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../../components/ui/select";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "../../components/ui/dropdown-menu";
import {
  Download, FileSpreadsheet, FileText, RefreshCw, File, FileJson,
  Building2, Layers, TrendingUp, AlertTriangle, Filter, CheckCircle2,
  Info, Sparkles, Hash, BarChart3,
} from "lucide-react";
import { useAuth } from "../../App";
import { toast } from "sonner";
import { saveAs } from "file-saver";
import * as XLSX from "xlsx";
import { jsPDF } from "jspdf";
import { applyPlugin } from "jspdf-autotable";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  ResponsiveContainer, PieChart, Pie, Cell, LineChart, Line,
} from "recharts";

applyPlugin(jsPDF);

const COLORS = [
  "#0ea5e9", "#10b981", "#8b5cf6", "#f59e0b", "#ef4444",
  "#ec4899", "#14b8a6", "#f97316", "#6366f1", "#84cc16",
];

const CATEGORIA_COLORS = {
  A: "#1d4ed8", B: "#0891b2", C: "#0d9488", D: "#16a34a", E: "#ca8a04", F: "#dc2626",
};

const ANALISIS_BG = {
  warning: "bg-amber-50 border-amber-200 text-amber-900",
  info: "bg-blue-50 border-blue-200 text-blue-900",
  success: "bg-emerald-50 border-emerald-200 text-emerald-900",
};

const ANALISIS_ICON = {
  warning: AlertTriangle,
  info: Info,
  success: CheckCircle2,
};


const TablaValoresConstruccion = () => {
  const { API, token } = useAuth();
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [filters, setFilters] = useState({
    tipologia: "",
    estado: "todos",
    solo_en_uso: false,
  });
  const [tableSearch, setTableSearch] = useState("");

  const fetchData = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filters.tipologia.trim()) params.tipologia = filters.tipologia.trim();
      params.estado = filters.estado;
      params.solo_en_uso = filters.solo_en_uso;
      const r = await axios.get(`${API}/reportes/tabla-tvc`, { params, headers });
      setData(r.data);
    } catch (err) {
      toast.error("No se pudo cargar el reporte TVC");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.estado, filters.solo_en_uso]);

  const onApplyFilters = () => fetchData();
  const onResetFilters = () => {
    setFilters({ tipologia: "", estado: "todos", solo_en_uso: false });
    setTableSearch("");
  };

  const filteredFilas = useMemo(() => {
    const filas = data?.filas || [];
    if (!tableSearch.trim()) return filas;
    const q = tableSearch.toLowerCase();
    return filas.filter((f) =>
      [f.tabla, f.tipologia, f.descripcion]
        .some((s) => (s || "").toString().toLowerCase().includes(q))
    );
  }, [data, tableSearch]);

  // ===== EXPORT =====
  const exportRows = () =>
    filteredFilas.map((f) => ({
      "Tabla": f.tabla,
      "Tipología": f.tipologia,
      "Descripción": f.descripcion,
      "Estado": f.is_active ? "Activa" : "Inactiva",
      "Valor A": f.valor_a, "Valor B": f.valor_b, "Valor C": f.valor_c,
      "Valor D": f.valor_d, "Valor E": f.valor_e, "Valor F": f.valor_f,
      "Promedio": f.promedio_categoria,
      "Máximo": f.valor_maximo,
      "Fichas Asociadas": f.fichas_asociadas,
      "Área Construcción Total (m²)": f.area_construccion_total_m2,
      "Valor Catastral Total (Bs)": f.valor_catastral_total_bs,
    }));

  const exportToExcel = () => {
    const rows = exportRows();
    if (!rows.length) { toast.error("No hay datos para exportar"); return; }
    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "TVC");
    XLSX.writeFile(wb, `reporte_tvc_${new Date().toISOString().split("T")[0]}.xlsx`);
    toast.success("Exportado a Excel");
  };

  const exportToPDF = () => {
    const rows = exportRows();
    if (!rows.length) { toast.error("No hay datos para exportar"); return; }
    const doc = new jsPDF("landscape");
    doc.setFontSize(16); doc.text("Reporte — Tabla de Valores de la Construcción (TVC)", 14, 18);
    doc.setFontSize(10); doc.text(`Fecha: ${new Date().toLocaleDateString("es-VE")} · Total: ${rows.length} tipología(s)`, 14, 26);
    if (data?.resumen) {
      const r = data.resumen;
      doc.setFontSize(9);
      doc.text(
        `Cobertura fichas: ${r.cobertura_pct}%  ·  En uso: ${r.tipologias_en_uso}/${r.total_tipologias}  ·  Huérfanas: ${r.tipologias_huerfanas}  ·  Promedio global: ${r.valor_promedio_global}`,
        14, 32
      );
    }
    doc.autoTable({
      head: [Object.keys(rows[0])],
      body: rows.map((r) => Object.values(r)),
      startY: 38,
      styles: { fontSize: 7 },
      headStyles: { fillColor: [29, 78, 216] },
    });
    doc.save(`reporte_tvc_${new Date().toISOString().split("T")[0]}.pdf`);
    toast.success("Exportado a PDF");
  };

  const exportToCSV = () => {
    const rows = exportRows();
    if (!rows.length) { toast.error("No hay datos"); return; }
    const h = Object.keys(rows[0]).join(",");
    const body = rows
      .map((r) => Object.values(r).map((v) => `"${(v ?? "").toString().replace(/"/g, '""')}"`).join(","))
      .join("\n");
    saveAs(
      new Blob(["\ufeff" + `${h}\n${body}`], { type: "text/csv;charset=utf-8" }),
      `reporte_tvc_${new Date().toISOString().split("T")[0]}.csv`
    );
    toast.success("Exportado a CSV");
  };

  const exportToJSON = () => {
    if (!data) { toast.error("No hay datos"); return; }
    saveAs(
      new Blob([JSON.stringify({ ...data, filas: filteredFilas }, null, 2)], { type: "application/json" }),
      `reporte_tvc_${new Date().toISOString().split("T")[0]}.json`
    );
    toast.success("Exportado a JSON");
  };

  const r = data?.resumen || {};
  const fmtNum = (n) => (n ?? 0).toLocaleString("es-VE");
  const fmtMoney = (n) => `Bs ${(n ?? 0).toLocaleString("es-VE", { minimumFractionDigits: 2 })}`;

  return (
    <div className="space-y-6" data-testid="reporte-tvc-page">
      {/* HEADER */}
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 bg-gradient-to-br from-blue-600 to-indigo-700 rounded-xl flex items-center justify-center shadow-md">
            <Building2 className="w-6 h-6 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: "Manrope, sans-serif" }}>
              Reporte — Tabla de Valores de la Construcción
            </h1>
            <p className="text-sm text-slate-500">
              Análisis dinámico de tipologías constructivas, valores por categoría y cobertura sobre {fmtNum(r.fichas_total)} fichas catastrales
            </p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={fetchData} disabled={loading} data-testid="btn-refresh">
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? "animate-spin" : ""}`} /> Actualizar
          </Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button className="bg-blue-700 hover:bg-blue-800 text-white" data-testid="btn-export">
                <Download className="w-4 h-4 mr-2" /> Exportar
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={exportToPDF} data-testid="btn-export-pdf">
                <FileText className="w-4 h-4 mr-2 text-red-600" /> PDF
              </DropdownMenuItem>
              <DropdownMenuItem onClick={exportToExcel} data-testid="btn-export-excel">
                <FileSpreadsheet className="w-4 h-4 mr-2 text-emerald-600" /> Excel
              </DropdownMenuItem>
              <DropdownMenuItem onClick={exportToCSV} data-testid="btn-export-csv">
                <File className="w-4 h-4 mr-2 text-slate-600" /> CSV
              </DropdownMenuItem>
              <DropdownMenuItem onClick={exportToJSON} data-testid="btn-export-json">
                <FileJson className="w-4 h-4 mr-2 text-blue-600" /> JSON
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {/* FILTROS */}
      <Card data-testid="filtros-card">
        <CardContent className="pt-5 pb-5">
          <div className="flex flex-wrap items-end gap-3">
            <div className="flex items-center gap-2 text-slate-700 font-semibold">
              <Filter className="w-4 h-4" /> Filtros
            </div>
            <div className="flex-1 min-w-[260px]">
              <Label className="text-xs text-slate-500 mb-1 block">Tipología o descripción</Label>
              <Input
                placeholder="Buscar tipología..."
                value={filters.tipologia}
                onChange={(e) => setFilters({ ...filters, tipologia: e.target.value })}
                onKeyDown={(e) => e.key === "Enter" && onApplyFilters()}
                data-testid="input-filtro-tipologia"
              />
            </div>
            <div>
              <Label className="text-xs text-slate-500 mb-1 block">Estado</Label>
              <Select value={filters.estado} onValueChange={(v) => setFilters({ ...filters, estado: v })}>
                <SelectTrigger className="w-[160px]" data-testid="select-estado">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="todos">Todas</SelectItem>
                  <SelectItem value="activos">Activas</SelectItem>
                  <SelectItem value="inactivos">Inactivas</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <label className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer pb-2"
                   data-testid="check-solo-en-uso">
              <input
                type="checkbox"
                checked={filters.solo_en_uso}
                onChange={(e) => setFilters({ ...filters, solo_en_uso: e.target.checked })}
                className="w-4 h-4 accent-blue-700"
              />
              Sólo tipologías con fichas
            </label>
            <Button onClick={onApplyFilters} className="bg-blue-700 text-white" data-testid="btn-aplicar">
              Aplicar
            </Button>
            <Button variant="outline" onClick={onResetFilters} data-testid="btn-reset">
              Limpiar
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        <KPICard label="Tipologías" value={fmtNum(r.total_tipologias)} icon={Layers} color="blue" testid="kpi-total" />
        <KPICard label="En Uso" value={fmtNum(r.tipologias_en_uso)} icon={CheckCircle2} color="emerald" testid="kpi-en-uso" />
        <KPICard label="Huérfanas" value={fmtNum(r.tipologias_huerfanas)} icon={AlertTriangle} color="amber" testid="kpi-huerfanas" />
        <KPICard label="Cobertura Fichas" value={`${r.cobertura_pct ?? 0}%`} icon={TrendingUp} color="indigo" testid="kpi-cobertura" />
        <KPICard label="Valor Promedio" value={fmtNum(r.valor_promedio_global)} icon={Hash} color="purple" testid="kpi-valor-promedio" />
        <KPICard label="Cat. más cara" value={r.categoria_mas_cara || "—"} icon={Sparkles} color="rose" testid="kpi-cat-cara" />
      </div>

      {/* ANÁLISIS */}
      {data?.analisis?.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="analisis-block">
          {data.analisis.map((a, i) => {
            const Icon = ANALISIS_ICON[a.tipo] || Info;
            return (
              <div key={i}
                   className={`flex gap-3 p-3 rounded-lg border ${ANALISIS_BG[a.tipo] || ANALISIS_BG.info}`}
                   data-testid={`analisis-${a.tipo}-${i}`}>
                <Icon className="w-5 h-5 flex-shrink-0 mt-0.5" />
                <p className="text-sm leading-snug">{a.texto}</p>
              </div>
            );
          })}
        </div>
      )}

      {/* CHARTS GRID */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Bar — top tipologías por fichas */}
        <Card data-testid="chart-barras-fichas">
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <BarChart3 className="w-4 h-4 text-blue-600" /> Top tipologías por fichas catastrales
            </CardTitle>
          </CardHeader>
          <CardContent>
            {data?.grafico_barras_fichas?.length > 0 ? (
              <ResponsiveContainer width="100%" height={320}>
                <BarChart data={data.grafico_barras_fichas} margin={{ top: 8, right: 16, bottom: 60, left: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="tipologia" tick={{ fontSize: 10 }} interval={0} angle={-30} textAnchor="end" />
                  <YAxis tick={{ fontSize: 11 }} />
                  <Tooltip />
                  <Bar dataKey="fichas" fill="#1d4ed8" radius={[4, 4, 0, 0]}>
                    {data.grafico_barras_fichas.map((_, i) => (
                      <Cell key={i} fill={COLORS[i % COLORS.length]} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <EmptyChart label="Sin tipologías con fichas asignadas" />
            )}
          </CardContent>
        </Card>

        {/* Pie — distribución de fichas */}
        <Card data-testid="chart-pie-distribucion">
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <PieIcon /> Distribución de fichas por tipología
            </CardTitle>
          </CardHeader>
          <CardContent>
            {data?.grafico_pie_distribucion?.length > 0 ? (
              <ResponsiveContainer width="100%" height={320}>
                <PieChart>
                  <Pie
                    data={data.grafico_pie_distribucion}
                    cx="50%" cy="50%" outerRadius={110}
                    dataKey="fichas" nameKey="tipologia"
                    label={({ tipologia, fichas }) => `${(tipologia || "").slice(0, 22)}: ${fichas}`}
                  >
                    {data.grafico_pie_distribucion.map((_, i) => (
                      <Cell key={i} fill={COLORS[i % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <EmptyChart label="Sin datos suficientes" />
            )}
          </CardContent>
        </Card>

        {/* Bar — promedio por categoría A..F */}
        <Card data-testid="chart-categorias">
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <TrendingUp className="w-4 h-4 text-indigo-600" /> Valor promedio y máximo por categoría (A — F)
            </CardTitle>
          </CardHeader>
          <CardContent>
            {data?.grafico_barras_categorias?.length > 0 ? (
              <ResponsiveContainer width="100%" height={320}>
                <BarChart data={data.grafico_barras_categorias} margin={{ top: 8, right: 16, bottom: 8, left: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="categoria" tick={{ fontSize: 12, fontWeight: 600 }} />
                  <YAxis tick={{ fontSize: 11 }} />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="promedio" name="Promedio" fill="#1d4ed8" radius={[4, 4, 0, 0]}>
                    {data.grafico_barras_categorias.map((row) => (
                      <Cell key={row.categoria} fill={CATEGORIA_COLORS[row.categoria] || "#1d4ed8"} />
                    ))}
                  </Bar>
                  <Bar dataKey="maximo" name="Máximo" fill="#94a3b8" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <EmptyChart label="Sin datos de categorías" />
            )}
          </CardContent>
        </Card>

        {/* Donut — activos vs inactivos */}
        <Card data-testid="chart-donut-estado">
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <CheckCircle2 className="w-4 h-4 text-emerald-600" /> Estado y uso del catálogo
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 gap-2">
              <ResponsiveContainer width="100%" height={260}>
                <PieChart>
                  <Pie
                    data={data?.grafico_donut_estado || []}
                    cx="50%" cy="50%" innerRadius={45} outerRadius={80}
                    dataKey="value" nameKey="name"
                    label={({ name, value }) => `${name}: ${value}`}
                  >
                    {(data?.grafico_donut_estado || []).map((row, i) => (
                      <Cell key={i} fill={row.color} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
              <ResponsiveContainer width="100%" height={260}>
                <PieChart>
                  <Pie
                    data={data?.grafico_uso || []}
                    cx="50%" cy="50%" innerRadius={45} outerRadius={80}
                    dataKey="value" nameKey="name"
                    label={({ name, value }) => `${name}: ${value}`}
                  >
                    {(data?.grafico_uso || []).map((row, i) => (
                      <Cell key={i} fill={row.color} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* TABLA */}
      <Card data-testid="tabla-detalle">
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <CardTitle className="text-base">Detalle de tipologías</CardTitle>
            <div className="flex items-center gap-3">
              <Input
                placeholder="Buscar en tabla..."
                className="w-[220px] h-8 text-sm"
                value={tableSearch}
                onChange={(e) => setTableSearch(e.target.value)}
                data-testid="input-busqueda-tabla"
              />
              <span className="text-xs text-slate-500" data-testid="contador-tabla">
                {filteredFilas.length} de {data?.filas?.length || 0}
              </span>
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex justify-center py-12">
              <RefreshCw className="w-8 h-8 animate-spin text-blue-600" />
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b bg-slate-50 text-slate-600 text-xs uppercase tracking-wide">
                    <th className="px-3 py-3 text-left">Tabla</th>
                    <th className="px-3 py-3 text-left">Tipología</th>
                    <th className="px-3 py-3 text-left">Descripción</th>
                    <th className="px-2 py-3 text-right">A</th>
                    <th className="px-2 py-3 text-right">B</th>
                    <th className="px-2 py-3 text-right">C</th>
                    <th className="px-2 py-3 text-right">D</th>
                    <th className="px-2 py-3 text-right">E</th>
                    <th className="px-2 py-3 text-right">F</th>
                    <th className="px-2 py-3 text-right">Promedio</th>
                    <th className="px-2 py-3 text-right">Fichas</th>
                    <th className="px-3 py-3 text-right">Área Const. (m²)</th>
                    <th className="px-3 py-3 text-right">Valor Catastral (Bs)</th>
                    <th className="px-2 py-3 text-center">Estado</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredFilas.length === 0 ? (
                    <tr>
                      <td colSpan={14} className="px-4 py-8 text-center text-slate-400">
                        Sin resultados con los filtros aplicados
                      </td>
                    </tr>
                  ) : filteredFilas.map((f) => (
                    <tr key={f.id} className="border-b hover:bg-slate-50/60" data-testid={`row-tvc-${f.id}`}>
                      <td className="px-3 py-2 font-mono text-blue-700 font-semibold">{f.tabla || "—"}</td>
                      <td className="px-3 py-2 font-semibold text-slate-800">{f.tipologia}</td>
                      <td className="px-3 py-2 text-slate-600">{f.descripcion}</td>
                      <td className="px-2 py-2 text-right font-mono">{f.valor_a || "—"}</td>
                      <td className="px-2 py-2 text-right font-mono">{f.valor_b || "—"}</td>
                      <td className="px-2 py-2 text-right font-mono">{f.valor_c || "—"}</td>
                      <td className="px-2 py-2 text-right font-mono">{f.valor_d || "—"}</td>
                      <td className="px-2 py-2 text-right font-mono">{f.valor_e || "—"}</td>
                      <td className="px-2 py-2 text-right font-mono">{f.valor_f || "—"}</td>
                      <td className="px-2 py-2 text-right font-mono font-semibold text-indigo-700">
                        {f.promedio_categoria}
                      </td>
                      <td className="px-2 py-2 text-right">
                        <span className={`font-bold ${f.fichas_asociadas > 0 ? "text-blue-700" : "text-slate-300"}`}>
                          {f.fichas_asociadas}
                        </span>
                      </td>
                      <td className="px-3 py-2 text-right text-slate-700">
                        {f.area_construccion_total_m2.toLocaleString("es-VE", { minimumFractionDigits: 2 })}
                      </td>
                      <td className="px-3 py-2 text-right text-slate-800 font-medium">
                        {f.valor_catastral_total_bs.toLocaleString("es-VE", { minimumFractionDigits: 2 })}
                      </td>
                      <td className="px-2 py-2 text-center">
                        {f.is_active ? (
                          <span className="inline-flex px-2 py-0.5 rounded-full text-[10px] font-semibold bg-emerald-100 text-emerald-700">
                            Activa
                          </span>
                        ) : (
                          <span className="inline-flex px-2 py-0.5 rounded-full text-[10px] font-semibold bg-slate-200 text-slate-700">
                            Inactiva
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
                {filteredFilas.length > 0 && (
                  <tfoot>
                    <tr className="bg-blue-50 font-semibold border-t-2 border-blue-200">
                      <td colSpan={10} className="px-3 py-3 text-right text-blue-900">Totales:</td>
                      <td className="px-2 py-3 text-right text-blue-900">
                        {fmtNum(filteredFilas.reduce((a, f) => a + f.fichas_asociadas, 0))}
                      </td>
                      <td className="px-3 py-3 text-right text-blue-900">
                        {filteredFilas.reduce((a, f) => a + f.area_construccion_total_m2, 0)
                          .toLocaleString("es-VE", { minimumFractionDigits: 2 })}
                      </td>
                      <td className="px-3 py-3 text-right text-blue-900">
                        {filteredFilas.reduce((a, f) => a + f.valor_catastral_total_bs, 0)
                          .toLocaleString("es-VE", { minimumFractionDigits: 2 })}
                      </td>
                      <td></td>
                    </tr>
                  </tfoot>
                )}
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};


// ===== AUX =====
const KPICard = ({ label, value, icon: Icon, color, testid }) => {
  const colors = {
    blue: { border: "border-l-blue-600", icon: "text-blue-600", bg: "bg-blue-50" },
    emerald: { border: "border-l-emerald-600", icon: "text-emerald-600", bg: "bg-emerald-50" },
    amber: { border: "border-l-amber-500", icon: "text-amber-600", bg: "bg-amber-50" },
    indigo: { border: "border-l-indigo-600", icon: "text-indigo-600", bg: "bg-indigo-50" },
    purple: { border: "border-l-purple-600", icon: "text-purple-600", bg: "bg-purple-50" },
    rose: { border: "border-l-rose-600", icon: "text-rose-600", bg: "bg-rose-50" },
  }[color] || { border: "border-l-slate-500", icon: "text-slate-600", bg: "bg-slate-50" };
  return (
    <Card className={`border-l-4 ${colors.border}`} data-testid={testid}>
      <CardContent className="pt-4 pb-4">
        <div className="flex items-center justify-between gap-2">
          <div className="min-w-0">
            <p className="text-[10px] uppercase tracking-wider text-slate-500 font-medium truncate">{label}</p>
            <p className="text-xl font-bold text-slate-900 mt-1 truncate">{value}</p>
          </div>
          <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${colors.bg}`}>
            <Icon className={`w-4 h-4 ${colors.icon}`} />
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

const PieIcon = () => (
  <svg className="w-4 h-4 text-emerald-600" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
    <path d="M21 12a9 9 0 11-9-9v9z"/><path d="M3.6 9A9 9 0 0112 3v9z"/>
  </svg>
);

const EmptyChart = ({ label }) => (
  <div className="h-64 flex items-center justify-center text-slate-400 text-sm">{label}</div>
);


export default TablaValoresConstruccion;
